import {
  createVidRange,
  dumpRange,
  type VidRange,
  type VidRangeData,
} from "~/units/scripture";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

/**
 * A pinia store for the verse preview dialog that pops up when clicking on verse references.
 *
 * The dialog needs to be global because it's invalid HTML to have `div.v-dialog__container` inside `span` or `p`.
 * It's also good to have one dialog instead of many, to reduce the total size of the markup.
 */
export const useVersePreviewStore = defineStore(
  "versePreview",
  () => {
    const getInit = () => ({
      rangeData: null,
      dialogOpen: false,
      inModalComment: false,
    });
    const init = getInit();

    /**
     * The VID range data.
     */
    const rangeData = ref<VidRangeData | null>(init.rangeData);

    /**
     * The VID range object.
     */
    const range = computed<VidRange | null>(() => {
      const data = rangeData.value;
      return data ? createVidRange(data) : null;
    });

    /**
     * The dialog's open/close model.
     */
    const dialogOpen = ref<boolean>(init.dialogOpen);

    /**
     * Know if used on modalComment view context
     */
    const inModalComment = ref<boolean>(init.inModalComment);

    function preview(
      range: VidRange,
      openFromModalComment: boolean | undefined = false,
    ) {
      rangeData.value = dumpRange(range);
      dialogOpen.value = true;
      inModalComment.value = openFromModalComment;
    }

    function close() {
      dialogOpen.value = false;
      rangeData.value = null;
      inModalComment.value = false;
    }

    function $reset() {
      const init = getInit();
      rangeData.value = init.rangeData;
      dialogOpen.value = init.dialogOpen;
      inModalComment.value = init.inModalComment;
    }

    return {
      rangeData,
      range,
      dialogOpen,
      inModalComment,
      preview,
      close,
      $reset,
    };
  },
  {
    persist: false,
  },
);

export type VersePreviewStore = ReturnType<typeof useVersePreviewStore>;
